import {FC, createContext, useContext, useMemo, useState} from 'react';
import {IntlProvider, useIntl} from 'react-intl';
import '@formatjs/intl-pluralrules/polyfill-force';
import '@formatjs/intl-pluralrules/locale-data/en';
import '@formatjs/intl-pluralrules/locale-data/ja';
import '@formatjs/intl-numberformat/polyfill-force';
import '@formatjs/intl-numberformat/locale-data/en';
import '@formatjs/intl-numberformat/locale-data/ja';
import '@formatjs/intl-datetimeformat/polyfill-force';
import '@formatjs/intl-datetimeformat/add-all-tz';
import '@formatjs/intl-datetimeformat/locale-data/en';
import '@formatjs/intl-datetimeformat/locale-data/ja';
import '@formatjs/intl-relativetimeformat/polyfill-force';
import '@formatjs/intl-relativetimeformat/locale-data/en';
import '@formatjs/intl-relativetimeformat/locale-data/ja';

import {WithChildren} from '../helpers';
import enStrings from '../../../assets/strings/en.json';
import jaStrings from '../../../assets/strings/ja.json';

const I18N_CONFIG_KEY = 'i18nSelectedLang';
type Lang = 'en' | 'ja';
const defaultLang: Lang = 'ja';
type ChangeLang = (lang: Lang) => void;
type Values = Record<string, string | number | boolean | null | undefined | Date>;

type I18nContextProps = {
	selectedLang: 'en' | 'ja';
	i18n: (key: string, values?: Values) => string;
	changeLang: ChangeLang;
	isJapanese: boolean;
};

const initialState: I18nContextProps = {
	selectedLang: defaultLang,
	i18n: (key: string, values?: Values) => {
		console.log(values);
		return key;
	},
	changeLang: (lang: Lang) => {
		console.log(lang);
	},
	isJapanese: true,
};

const setLangToLocal = (lang: Lang) => {
	localStorage.setItem(I18N_CONFIG_KEY, lang);
	window.location.reload();
};

const getLocalLang = (): Lang => {
	const lang = localStorage.getItem(I18N_CONFIG_KEY);

	return lang ? (lang as Lang) : defaultLang;
};

const I18nContext = createContext<I18nContextProps>(initialState);

const useI18n = () => {
	return useContext(I18nContext);
};

const allStrings = {
	en: enStrings,
	ja: jaStrings,
};

const I18nProviderInternal: FC<WithChildren & {changeLang: ChangeLang}> = ({children, changeLang}) => {
	const intl = useIntl();

	const selectedLang = intl.locale as Lang;

	const value = useMemo(
		() => ({
			selectedLang,
			i18n: (key: string, values?: Values) => intl.formatMessage({id: key}, values),
			changeLang,
			isJapanese: selectedLang === 'ja',
		}),
		[intl, changeLang, selectedLang],
	);

	return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

const I18nProvider: FC<WithChildren> = ({children}) => {
	const [currentLang, setCurrentLang] = useState<Lang>(getLocalLang());

	const changeLang = (lang: Lang) => {
		setLangToLocal(lang);
		setCurrentLang(lang);
	};

	const messages = Object.assign(allStrings.en, allStrings[currentLang]);

	return (
		<IntlProvider locale={currentLang} messages={messages}>
			<I18nProviderInternal changeLang={changeLang}>{children}</I18nProviderInternal>
		</IntlProvider>
	);
};

export {I18nProvider, useI18n};
